import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpHeaders,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import {
  AuthenticationSelectors,
  ConfigService,
  isErrorHandledByInterceptor,
  isNetworkError,
} from '@orion-platform/core';
import { Column } from '@orion-platform/datagrid';
import { environment } from '@orion/environment';
import { Helper } from 'apps/orion/src/app/helper/helper';
import { ApiQueryParams } from 'apps/orion/src/app/models/app-config.model';
import { NotificationService } from 'libs/core/src/lib/services';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { RequestStatus } from '../models/commit.model';
import {
  GenerateInstructionsFilter,
  Instruction,
  InstructionResponse,
  ModifiedInstructionResponse,
} from '../models/instructions.model';
import {
  DownloadProps,
  DynamicDateKey,
  EnumShipmentFieldStructure,
  InstructionDetail,
  InstructionPayload,
  ShipmentDeliveriesDetail,
  ShipmentFilterUIModel,
  ShipmentListField,
  ShipmentsFilterRequest,
  TemplateDetail,
  TemplatePayload,
  UploadShipmentDocumentPayload,
  UploadShipmentInternalDocumentPayload,
} from '../models/shipments.model';
import { DocumentResponse, ModifiedDocumentResponse } from '../models/upload-documents.model';
import {
  SHIPMENTS_COLUMN_LIST,
  SHIPMENTS_DETAIL_COLUMN_LIST,
  SHIPMENTS_DETAIL_FIELDS_KEY,
  SHIPMENTS_FIELDS_KEY,
} from '../pages/shipments/shipments.consts';
import { UploadDocumentsFilter } from '../pages/upload-documents/upload-documents-filter-form/upload-documents-filter-form.types';
import { FormSetting } from '../store/carriers-list/carriers-list.types';
import { DataGridResult } from '../store/partnumbers-list/partnumbers-list.types';
import {
  ListDocumentInfosResponse,
  Shipment,
  ShipmentFilterForm,
  ShipmentHistory,
} from '../store/shipments/shipments.types';
import { Plants } from '../types';
import { ColumnsService } from './columns.service';
import { ColumnsState } from '../types/columns.types';
import { CommonService } from './common.service';
import { FileUploadService } from './file-upload.service';
import { SupplyVisibilityService } from './supply-visibility.service';

@Injectable({
  providedIn: 'root',
})
export class ShipmentsService {
  private apiUrl!: string;

  private static readonly ORCH_PLANTS: readonly Plants[] = [Plants.MULTI, Plants.JUSDA, Plants.CTP];

  private configService = inject(ConfigService);
  private http = inject(HttpClient);
  private store = inject(Store);
  private readonly fileUpload = inject(FileUploadService);
  private readonly columns = inject(ColumnsService);
  private notificationService = inject(NotificationService);
  private commonService = inject(CommonService);
  private supplyVisibility = inject(SupplyVisibilityService);

  $upn: Observable<string> = this.store.select(AuthenticationSelectors.upn);
  emailId!: string;

  activePlant$: Observable<string> = this.store.select(AuthenticationSelectors.activePlant);

  private readonly shipmentsDetails = new BehaviorSubject<null | Shipment>(null);
  private readonly isSearchVisible = new Subject<unknown>();

  get currentShipments(): null | Shipment {
    return this.shipmentsDetails.value;
  }

  private readonly documentDetail = new Subject<ModifiedDocumentResponse | null>();
  currentDocument: ModifiedDocumentResponse | null = null;
  private readonly instructionDetails = new Subject<ModifiedInstructionResponse | null>();
  currentInstruction: ModifiedInstructionResponse | null = null;

  private shipmentDocumentDetail!: ModifiedDocumentResponse[] | undefined;

  apiBase = environment.modulesBaseUrl.materialManagement;

  constructor() {
    this.apiUrl = this.configService.getSettings('apiBaseUrl') as string;
  }

  get plant(): Plants {
    return this.store.selectSnapshot(AuthenticationSelectors.activePlant) as Plants;
  }

  get canBeOrch(): boolean {
    return ShipmentsService.ORCH_PLANTS.includes(this.plant as Plants);
  }

  /**
   * The shipments columns. `ShipmentListField` narrows itself by the plant's
   * fields, so the loader is left to merge the user's saved choice over it.
   * @returns {Observable<ColumnsState>} The columns, and whether they are still the defaults.
   */
  getDefaultColumns = (): Observable<ColumnsState> =>
    this.columns.load({
      buildDefaults: (formFields: string[]): Column[] =>
        new ShipmentListField(EnumShipmentFieldStructure.ShipmentTable, formFields)
          .fields as unknown as Column[],
      columnListKey: SHIPMENTS_COLUMN_LIST,
      formFields$: this.getDefaultFormSettingFields(SHIPMENTS_FIELDS_KEY),
    });

  /**
   * The columns of the grid nested inside a shipment.
   *
   * Its form key is optional — an unknown key means no restriction. The loader
   * treats a failed lookup as no fields, and with none `compareByFieldOrder`
   * falls back to the schema's own order.
   * @returns {Observable<ColumnsState>} The columns, and whether they are still the defaults.
   */
  getDefaultDetailColumns = (): Observable<ColumnsState> =>
    this.columns.load({
      buildDefaults: (formFields: string[]): Column[] =>
        new ShipmentListField(
          EnumShipmentFieldStructure.NestedDetailsTable,
          formFields.length ? formFields : undefined,
        ).fields as unknown as Column[],
      columnListKey: SHIPMENTS_DETAIL_COLUMN_LIST,
      formFields$: this.getDefaultFormSettingFields(SHIPMENTS_DETAIL_FIELDS_KEY),
    });

  getUserTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  getShipmentsDetail(): Observable<null | Shipment> {
    return this.shipmentsDetails.asObservable();
  }

  setShipmentsDetail(data: null | Shipment = null): void {
    this.shipmentsDetails.next(data);
  }

  getDocumentDetail(): Observable<ModifiedDocumentResponse | null> {
    return this.documentDetail.asObservable();
  }

  setDocumentDetail(data: ModifiedDocumentResponse): void {
    this.currentDocument = data;
    this.documentDetail.next(data);
  }

  getInstructionDetail(): Observable<ModifiedInstructionResponse | null> {
    return this.instructionDetails.asObservable();
  }

  setInstructionDetail(data: ModifiedInstructionResponse | null = null): void {
    this.currentInstruction = data;
    this.instructionDetails.next(data);
  }

  getShipmentsListReady(): Observable<unknown> {
    return this.isSearchVisible.asObservable();
  }

  setShipmentsListReady(data?: unknown): void {
    this.isSearchVisible.next(data);
  }

  getDocumentsListReady(): Observable<unknown> {
    return this.isSearchVisible.asObservable();
  }

  setDocumentsListReady(data?: unknown): void {
    this.isSearchVisible.next(data);
  }

  getInstructionsListReady(): Observable<unknown> {
    return this.isSearchVisible.asObservable();
  }

  setInstructionsListReady(data?: unknown): void {
    this.isSearchVisible.next(data);
  }

  getShipmentDocumentDetail(): ModifiedDocumentResponse[] | undefined {
    return this.shipmentDocumentDetail;
  }

  setShipmentDocumentDetail(data?: ModifiedDocumentResponse[]): void {
    this.shipmentDocumentDetail = data;
  }

  private shipmentId!: null | string;

  setShipmentId(shipmentId: null | string): void {
    this.shipmentId = shipmentId;
  }

  getShipmentId(): null | string {
    return this.shipmentId;
  }

  private instructionId!: string;

  setInstructionId(instructionId: string): void {
    this.instructionId = instructionId;
  }

  getInstructionId(): string {
    return this.instructionId;
  }

  private shipmentData = new Subject<{ shipment: Shipment }>();

  getShipmentDataToOpen(): Observable<{ shipment: Shipment }> {
    return this.shipmentData.asObservable();
  }

  setShipmentDataToOpen(data: { shipment: Shipment }): void {
    this.shipmentData.next(data);
  }

  private documentData = new Subject<{ document: ModifiedDocumentResponse }>();

  getDocumentDataToOpen(): Observable<{ document: ModifiedDocumentResponse }> {
    return this.documentData.asObservable();
  }

  setDocumentDataToOpen(data: { document: ModifiedDocumentResponse }): void {
    this.documentData.next(data);
  }

  errorMessageHandling(error: HttpErrorResponse, msg: string): void {
    if (isNetworkError(error) || isErrorHandledByInterceptor(error)) return;

    if (error.error instanceof Blob && error.error.type === 'application/json') {
      const reader = new FileReader();
      reader.onload = (): void => {
        const errorText = reader.result as string;
        const parsedError = JSON.parse(errorText);
        // Display the detail message from the error
        Helper.showError(this.notificationService, parsedError, msg);
      };
      reader.readAsText(error.error);
    } else {
      // Handle non-Blob errors (fallback)
      Helper.showError(this.notificationService, error, msg);
    }
  }

  //NOTE - Shipments
  getApiUrlShipments(): string {
    return `${this.apiUrl}${environment.modulesBaseUrl.materialManagement.shipments}`;
  }

  getApiUrlShipmentsMRN(): string {
    const url = this.canBeOrch
      ? `${this.apiUrl}${environment.modulesBaseUrl.materialManagement.shipments}orch`
      : `${this.apiUrl}${environment.modulesBaseUrl.materialManagement.shipments}`;
    return url.concat('/', 'MRN');
  }

  getShipmentsByFilter = (request: ShipmentsFilterRequest): Observable<Shipment[]> => {
    // Construct the shipments URL.
    const url = `${this.apiUrl}${this.getUrlPrefix()}${this.apiBase.getShipmentsByFilter}`;
    // Check if the active plants is MULTI plant.
    const multiPlant = this.store.selectSnapshot(AuthenticationSelectors.isMultiPlant);
    // Get request and plants applied to a widget.
    const { plants, ...rest } = request;
    // Set request tenants headers if MULTI plant is active.
    const headers = new HttpHeaders({
      ...(multiPlant ? { 'Request-Tenants': plants || [] } : {}),
    });

    return this.http.post<Shipment[]>(url, rest, { headers });
  };

  downloadXlsxShipments = (vars: DownloadProps): Observable<Blob> => {
    const url = `${this.apiUrl}${this.getUrlPrefix()}${this.apiBase.downloadXlsxShipments}`;
    return <Observable<Blob>>this.http.post(url, vars, { responseType: 'blob' });
  };

  getUrlPrefix(): string {
    return this.canBeOrch ? this.apiBase.shipmentsOrch : this.apiBase.shipments;
  }

  getApiUrlShipmentsByPlant(): string {
    return `${this.getApiUrlShipments()}__orchSuffix__`;
  }

  splitWith(data: string | string[] | undefined, symbol = /,/): string[] {
    if (!data) return [];

    let stringData = '';
    if (Array.isArray(data)) {
      stringData = data.join(',');
    } else {
      stringData = String(data);
    }

    return stringData
      .split(symbol)
      .map((c): string => c.trim())
      .filter((c): boolean => c !== '');
  }

  getDefaultFormSettingFields(formkey: string): Observable<string[]> {
    const url = new URL(`${this.getApiUrlShipmentsByPlant()}/getDefaultFormSettingFields`);
    url.searchParams.append('formKey', formkey);
    return this.http.get<string[]>(url.toString());
  }

  getDefaultFormSettingFormKeys(): Observable<string[]> {
    return this.http.get<string[]>(
      `${this.getApiUrlShipmentsByPlant()}/GetDefaultFormSettingFormKeys`,
    );
  }

  getDefaultFormSettings(formKey: string): Observable<FormSetting[]> {
    let params = new HttpParams();
    params = params.append('formKey', formKey);
    return this.http.get<FormSetting[]>(
      `${this.getApiUrlShipmentsByPlant()}/GetDefaultFormSettings`,
      {
        params: params,
      },
    );
  }

  updateDefaultFormSettings(formKey: string, payload: FormSetting[]): Observable<FormSetting[]> {
    let params = new HttpParams();
    params = params.append('formKey', formKey);
    return this.http.post<FormSetting[]>(
      `${this.getApiUrlShipmentsByPlant()}/updateDefaultFormSettings`,
      payload,
      {
        params: params,
      },
    );
  }

  getShipments(
    filterParam: ShipmentFilterForm | undefined,
    data: ApiQueryParams,
  ): Observable<DataGridResult<Shipment>> {
    const plant = this.store.selectSnapshot(AuthenticationSelectors.activePlant);
    this.$upn.subscribe((email): void => {
      this.emailId = email;
    });
    const filter = filterParam || {};
    const dtoFilter: ShipmentFilterUIModel = {
      contacts: filter.onlyMyShipments ? this.splitWith(this.emailId) : [],
      containerNumbers: this.splitWith(filter.containerNumbers),
      flags: this.splitWith(filter.flags),
      invoiceNumbers: this.splitWith(filter.invoiceNumbers),
      requestNumbers: this.splitWith(filter.requestNumbers),
      slotCodes: this.splitWith(filter.slotCodes),
      status: this.splitWith(filter.status),
      trackNumbers: this.splitWith(filter.trackNumbers),
      warehouses: this.splitWith(filter.warehouses),
    };

    if (filter.dateFrom) {
      const key = `${filter.dateType}From` as DynamicDateKey;
      dtoFilter[key] = this.commonService.getUTCDateStr(filter.dateFrom);
    }

    if (filter.dateTo) {
      const key = `${filter.dateType}To` as DynamicDateKey;
      dtoFilter[key] = this.commonService.getUTCDateStr(filter.dateTo);
    }

    if (plant == 'MULTI' && filter.plants) {
      dtoFilter['plants'] = Array.isArray(filter.plants) ? filter.plants : [filter.plants];
    }

    let params = new HttpParams();
    if (data) {
      if (data.skip !== null && data.skip !== undefined) {
        params = params.append('$skip', data.skip);
      }
      if (data.top !== null && data.top !== undefined) {
        params = params.append('$top', data.top);
      }
      if (data.sort !== null && data.sort !== undefined) {
        params = params.append('$orderby', data.sort as string);
      }
      if (data.searchValue) {
        params = params.append('searchText', data.searchValue);
      }
    }

    return <Observable<DataGridResult<Shipment>>>this.http.post(
      this.getApiUrlShipmentsByPlant(),
      dtoFilter,
      {
        params: params,
      },
    );
  }

  getShipmentByID(id: string): Observable<null | Shipment> {
    return <Observable<null | Shipment>>this.http.get(`${this.getApiUrlShipmentsByPlant()}/${id}`);
  }

  getDeliveryTerms(): Observable<string[]> {
    const url = `${this.getApiUrlShipmentsByPlant()}/DeliveryTerms`;
    return <Observable<string[]>>this.http.get(url);
  }

  addShipmentFlag(data: { flag: string; id: string }): Observable<unknown> {
    return <Observable<unknown>>(
      this.http.put(`${this.getApiUrlShipmentsByPlant()}/${data.id}/Flags/${data.flag}`, null)
    );
  }

  deleteShipmentFlag(data: { flag: string; id: string }): Observable<unknown> {
    return <Observable<unknown>>(
      this.http.delete(`${this.getApiUrlShipmentsByPlant()}/${data.id}/Flags/${data.flag}`)
    );
  }

  download(filterParam: ShipmentFilterForm | undefined, columns: string[]): Observable<Blob> {
    this.$upn.subscribe((email): void => {
      this.emailId = email;
    });
    const filter = filterParam || {};

    const dtoFilter: ShipmentFilterUIModel = {
      contacts: filter.onlyMyShipments ? this.splitWith(this.emailId) : [],
      containerNumbers: this.splitWith(filter.containerNumbers),
      fields: columns,
      flags: this.splitWith(filter.flags),
      requestNumbers: this.splitWith(filter.requestNumbers),
      slotCodes: this.splitWith(filter.slotCodes),
      status: this.splitWith(filter.status),
      trackNumbers: this.splitWith(filter.trackNumbers),
      type: 'XLSX',
      warehouses: this.splitWith(filter.warehouses),
    };

    if (filter.sorts) {
      dtoFilter.sorts = filter.sorts;
    }

    if (filter.dateFrom) {
      const key = `${filter.dateType}From` as DynamicDateKey;
      dtoFilter[key] = this.commonService.getUTCDateStr(filter.dateFrom);
    }

    if (filter.dateTo) {
      const key = `${filter.dateType}To` as DynamicDateKey;
      dtoFilter[key] = this.commonService.getUTCDateStr(filter.dateTo);
    }

    return this.http.post(
      `${this.getApiUrlShipmentsByPlant()}/${'GetUploadShipments'}`,
      dtoFilter,
      {
        responseType: 'blob',
      },
    );
  }

  downloadInvoicePdf(id: string): Observable<Blob> {
    return this.http.get(`${this.getApiUrlShipmentsByPlant()}/${id}/invoice`, {
      responseType: 'blob',
    });
  }

  downloadMultipleInvoicesPdf(id: string[]): Observable<Blob> {
    return <Observable<Blob>>this.http.post(`${this.getApiUrlShipmentsByPlant()}/invoices`, id, {
      responseType: 'blob',
    });
  }

  // NOTE - Shipment Documents
  getApiUrlShipmentDocuments(id: string): string {
    return `${this.getApiUrlShipmentsByPlant()}/${id}/Documents`;
  }

  getShipmentDocumentsWithTypes(id: string): Observable<ListDocumentInfosResponse[]> {
    return <Observable<ListDocumentInfosResponse[]>>(
      this.http.get(
        `${this.getApiUrlShipmentsByPlant()}/${encodeURIComponent(id)}/ListDocumentInfos`,
      )
    );
  }

  uploadShipmentDocuments(
    id: string,
    data: UploadShipmentDocumentPayload,
  ): Observable<HttpEvent<string>> {
    const formData = new FormData();

    // If multiple files:
    if (Array.isArray(data.content)) {
      data.content.forEach((file: File): void => {
        formData.append('Content', file); // multiple "Content" keys
      });
    } else {
      formData.append('Content', data.content);
    }

    formData.append('PurchaseOrderNumbers', data.purchaseOrderNumber);
    formData.append('Types', data.types);

    return <Observable<HttpEvent<string>>>this.http.post(
      `${this.getApiUrlShipmentDocuments(id)}`,
      formData,
      {
        observe: 'events',
        reportProgress: true,
        responseType: 'text',
      },
    );
  }

  downloadShipmentDocuments(id: string, docName: string): Observable<unknown> {
    const encodedName = encodeURIComponent(String(docName || ''));
    return <Observable<unknown>>this.http.get(
      `${this.getApiUrlShipmentDocuments(id)}/${encodedName}`,
      {
        responseType: 'blob',
      },
    );
  }

  downloadAllShipmentDocuments(shipmentId: string): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.getApiUrlShipmentDocuments(shipmentId)}`, {
      observe: 'response',
      responseType: 'blob',
    });
  }

  deleteShipmentDocuments = (shipmentId: string, fileName: string): Observable<unknown> => {
    const encodedName = encodeURIComponent(String(fileName || ''));
    const url = `${this.getApiUrlShipmentDocuments(shipmentId)}/${encodedName}`;
    return this.http.delete(url);
  };

  // NOTE - Shipment InternalDocuments
  getApiUrlShipmentInternalDocuments(id: string): string {
    return `${this.getApiUrlShipmentsByPlant()}/${id}/InternalDocuments`;
  }

  getShipmentInternalDocuments(id: string): Observable<ListDocumentInfosResponse[]> {
    return <Observable<ListDocumentInfosResponse[]>>(
      this.http.get(`${this.getApiUrlShipmentsByPlant()}/${id}/ListInternalDocumentInfos`)
    );
  }

  uploadShipmentInternalDocuments(
    id: string,
    data: UploadShipmentInternalDocumentPayload,
  ): Observable<HttpEvent<string>> {
    const formData = new FormData();
    // If multiple files:
    if (Array.isArray(data.content)) {
      data.content.forEach((file: File): void => {
        formData.append('Content', file);
      });
    } else {
      formData.append('Content', data.content);
    }
    formData.append('Description', data.description);
    return <Observable<HttpEvent<string>>>this.http.post(
      `${this.getApiUrlShipmentInternalDocuments(id)}`,
      formData,
      {
        observe: 'events',
        reportProgress: true,
        responseType: 'text',
      },
    );
  }

  downloadShipmentInternalDocuments(id: string, docName: string): Observable<Blob> {
    const encodedName = encodeURIComponent(String(docName || ''));
    return <Observable<Blob>>this.http.get(
      `${this.getApiUrlShipmentInternalDocuments(id)}/${encodedName}`,
      {
        responseType: 'blob',
      },
    );
  }

  downloadAllShipmentInternalDocuments(shipmentId: string): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.getApiUrlShipmentInternalDocuments(shipmentId)}`, {
      observe: 'response',
      responseType: 'blob',
    });
  }

  deleteShipmentInternalDocuments(id: string, docName: string): Observable<unknown> {
    const encodedName = encodeURIComponent(String(docName || ''));
    return <Observable<unknown>>(
      this.http.delete(`${this.getApiUrlShipmentInternalDocuments(id)}/${encodedName}`)
    );
  }

  // NOTE - Shipment Details

  getShipmentDeliveriesDetail(id: string): Observable<ShipmentDeliveriesDetail[]> {
    return <Observable<ShipmentDeliveriesDetail[]>>(
      this.http.get(`${this.getApiUrlShipmentsByPlant()}/${id}/deliveries`)
    );
  }

  // NOTE - Shipment History

  getShipmentHistory(id: string): Observable<DataGridResult<ShipmentHistory>> {
    return <Observable<DataGridResult<ShipmentHistory>>>(
      this.http.get(`${this.getApiUrlShipmentsByPlant()}/${id}/ShipmentHistory`)
    );
  }

  // NOTE - Shipment Instructions

  getShipmentInstructions(id: string): Observable<Instruction[]> {
    return this.http.get<Instruction[]>(`${this.getApiUrlShipmentsByPlant()}/${id}/instructions`);
  }

  generateInstructions(id: string, payload: InstructionPayload): Observable<unknown> {
    payload['timeZone'] = this.getUserTimezone();
    return <Observable<unknown>>(
      this.http.post(`${this.getApiUrlShipments()}/${id}/instructions/generate`, payload)
    );
  }

  getInstructionByID = (shipmentId: string, instructionId: string): Observable<Instruction> => {
    return <Observable<Instruction>>(
      this.http.get(`${this.getApiUrlShipments()}/${shipmentId}/instructions/${instructionId}`)
    );
  };

  getInstructionDetails(
    shipmentId: string,
    instructionId: string,
  ): Observable<InstructionDetail[]> {
    return <Observable<InstructionDetail[]>>(
      this.http.get(
        `${this.getApiUrlShipments()}/${shipmentId}/instructions/${instructionId}/details`,
      )
    );
  }

  updateInstructionDetails(
    shipmentId: string,
    instructionId: string,
    data: InstructionDetail,
  ): Observable<InstructionDetail> {
    return <Observable<InstructionDetail>>(
      this.http.put(
        `${this.getApiUrlShipments()}/${shipmentId}/instructions/${instructionId}/details/${
          data.id
        }`,
        data,
      )
    );
  }

  copyInstructionDetailToHeader(
    shipmentId: string,
    instructionId: string,
    detailId: string,
  ): Observable<unknown> {
    return <Observable<unknown>>(
      this.http.post(
        `${this.getApiUrlShipments()}/${shipmentId}/instructions/${instructionId}/details/${detailId}/copytoheaderRegenerateTemplate`,
        null,
      )
    );
  }

  regenerateTemplate(
    shipmentId: string,
    instructionId: string,
    payload: unknown = null,
  ): Observable<unknown> {
    const url = `${this.getApiUrlShipments()}/${shipmentId}/instructions/${instructionId}/regenerateTemplate`;
    return <Observable<unknown>>this.http.post(url, payload);
  }

  getTemplateDetail(shipmentId: string, instructionId: string): Observable<TemplateDetail> {
    return <Observable<TemplateDetail>>(
      this.http.get(
        `${this.getApiUrlShipments()}/${shipmentId}/instructions/${instructionId}/template`,
      )
    );
  }

  getInstructionTypes(): Observable<string[]> {
    return <Observable<string[]>>this.http.get(`${this.getApiUrlShipments()}/instructionTypes`);
  }

  saveInstructionTemplate(
    shipmentId: string,
    instructionId: string,
    data: TemplatePayload,
  ): Observable<TemplateDetail> {
    return <Observable<TemplateDetail>>(
      this.http.put(
        `${this.getApiUrlShipments()}/${shipmentId}/instructions/${instructionId}/template`,
        data,
      )
    );
  }

  sendInstruction(
    shipmentId: string,
    instructionId: string,
    data: TemplatePayload,
  ): Observable<TemplateDetail> {
    return <Observable<TemplateDetail>>(
      this.http.post(
        `${this.getApiUrlShipments()}/${shipmentId}/instructions/${instructionId}/send`,
        data,
      )
    );
  }
  // NOTE - shipmentsOrch

  getApiUrlShipmentsOrch(): string {
    return `${this.apiUrl}${environment.modulesBaseUrl.materialManagement.shipmentsOrch}`;
  }

  getDocuments(filter: UploadDocumentsFilter): Observable<DocumentResponse[]> {
    const dtoFilter = {
      fulltextSearch: filter.fulltextSearch,
      keys: this.splitWith(filter.shipments ? filter.shipments.toString() : filter.shipments),
    };

    return <Observable<DocumentResponse[]>>(
      this.http.post(`${this.getApiUrlShipmentsOrch()}/FindDocuments`, dtoFilter)
    );
  }

  getAndGenerateInstructions(
    filter: GenerateInstructionsFilter,
  ): Observable<InstructionResponse[]> {
    const dtoFilter = {
      DeliveryInfo: {
        date: Helper.formatDate(filter.date),
        regime: filter.regime,
      },
      fulltextSearch: filter.fulltextSearch,
      keys: this.splitWith(filter.shipments ? filter.shipments.toString() : filter.shipments),
    };

    return <Observable<InstructionResponse[]>>(
      this.http.post(`${this.getApiUrlShipmentsOrch()}/GenerateInstructions`, dtoFilter)
    );
  }

  sendInstructions(data: unknown): Observable<unknown> {
    return <Observable<unknown>>(
      this.http.post(`${this.getApiUrlShipmentsOrch()}/SendInstructions`, data)
    );
  }

  getDocumentList(id: string): Observable<ListDocumentInfosResponse[]> {
    return <Observable<ListDocumentInfosResponse[]>>(
      this.http.get(`${this.getApiUrlShipmentsOrch()}/${encodeURIComponent(id)}/ListDocumentInfos`)
    );
  }

  getApiUrlDocumentList(id: string): string {
    return `${this.getApiUrlShipmentsOrch()}/${encodeURIComponent(id)}/Documents`;
  }

  uploadDocument(id: string, data: UploadShipmentDocumentPayload): Observable<HttpEvent<string>> {
    const formData = new FormData();
    // If multiple files:
    if (Array.isArray(data.content)) {
      data.content.forEach((file: File): void => {
        formData.append('Content', file);
      });
    } else {
      formData.append('Content', data.content);
    }
    formData.append('PurchaseOrderNumbers', data.purchaseOrderNumber);
    formData.append('Types', data.types);
    if (data.carrierEtaDate) {
      formData.append('CarrierETADate', data.carrierEtaDate);
    }
    return <Observable<HttpEvent<string>>>this.http.post(
      `${this.getApiUrlDocumentList(id)}`,
      formData,
      {
        observe: 'events',
        reportProgress: true,
        responseType: 'text',
      },
    );
  }

  deleteDocument(id: string, docName: string): Observable<unknown> {
    const encodedName = encodeURIComponent(String(docName || ''));
    return <Observable<unknown>>(
      this.http.delete(`${this.getApiUrlDocumentList(id)}/${encodedName}`)
    );
  }

  downloadDocument(id: string, docName: string): Observable<Blob> {
    const encodedName = encodeURIComponent(String(docName || ''));
    return <Observable<Blob>>this.http.get(`${this.getApiUrlDocumentList(id)}/${encodedName}`, {
      responseType: 'blob',
    });
  }

  batchEditShipmentRequestStatus(requestHash: string): Observable<RequestStatus> {
    return <Observable<RequestStatus>>(
      this.http.get(`${this.getApiUrlShipmentsByPlant()}/-/RequestStatus/${requestHash}`)
    );
  }

  uploadShipmentsUpdate(fileName: string, file: File): Observable<unknown> {
    const url = `${this.getApiUrlShipmentsMRN()}/Upload`;
    return this.fileUpload.uploadFormData(url, fileName, file);
  }
}
